// Three.js 3D City Scene with Camera Controls
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { SkyWaterScene } from './sky-water-scene.js';
import { AudioSystem } from './audio-system.js';
import { SeagullFlock } from './seagull-flock.js';

// Production logging utility
const log = {
    debug: (message, ...args) => {
        if (process.env.NODE_ENV === 'development') {
            console.log(message, ...args);
        }
    },
    error: (message, ...args) => console.error(message, ...args),
    warn: (message, ...args) => console.warn(message, ...args),
    progress: (message, ...args) => console.log(message, ...args) // Always show progress
};

class CityScene {
    constructor() {
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.controls = null;
        this.cityModel = null;
        this.clock = new THREE.Clock();
        this.skyWaterScene = null;
        this.audioSystem = null;
        this.seagullFlock = null;
        
        // Device detection and responsive configuration
        this.deviceType = this.detectDeviceType();
        this.deviceConfig = this.getDeviceConfiguration();
        
        // Loading progress tracking
        this.loadingProgress = {
            current: 0,
            currentPercentage: 0, // Track current percentage to prevent reversal
            total: 11, // Total number of loading stages
            modelLoadingActive: false, // Flag to track if model loading is in progress
            stages: [
                'Initializing scene...',
                'Setting up lighting...',
                'Creating sky and water environment...',
                'Setting up seagull flock...',
                'Setting up audio environment...',
                'Adding sky life...',
                'Creating environment...',
                'Configuring controls...',
                'Loading Chromatic One Island...',
                'Finalizing setup...'
            ]
        };
        
        // Performance and timing configuration
        this.performanceConfig = {
            // Animation timings
            rotationDuration: 2500,
            transitionDuration: 1000,
            autoRotationResumeDelay: 5000,
            loadingStageDelay: 300,
            overlayUpdateDelay: 100,
            soundToggleDelay: 2000,
            popupDelay: 1000,
            
            // Frame rate optimization
            overlayUpdateThrottle: 10, // Update every 10th frame during rotation
            zoomUpdateThrottle: 100,   // Minimum 100ms between zoom updates
            
            // Performance thresholds
            complexityThreshold: 0.8,
            maxConcurrentTimers: 20
        };
        
        // Rotation configuration (device-specific)
        this.rotationConfig = {
            duration: this.performanceConfig.rotationDuration, 
            easing: 'easeInOutCubic',
            interruptible: true,
            zoomInDistance: this.deviceConfig.optimalDistance,
            normalDistance: this.deviceConfig.optimalDistance
        };
        
        // Auto-rotation configuration
        this.autoRotationConfig = {
            enabled: true,
            speed: 4,
            direction: 1,
            pauseOnInteraction: true,
            resumeDelay: this.performanceConfig.autoRotationResumeDelay,
            sequence: [270, 0, 90, 180], // About Us → Our Services → Our Projects → Contact Us
            sequenceIndex: 0
        };
        
        // Zoom limits configuration (device-specific)
        this.zoomLimits = {
            minDistance: this.deviceConfig.minDistance,
            maxDistance: this.deviceConfig.maxDistance,
            optimalDistance: this.deviceConfig.optimalDistance,
            zoomSpeed: this.deviceConfig.zoomSpeed
        };
        
        this.isRotating = false;
        this.rotationProgress = 0;
        this.rotationStartTime = 0;
        this.currentAngle = 270; // Track current camera angle (start at About Us)
        this.targetAngle = 0;
        this.rotationQueue = []; // Queue for sequential rotations
        this.startDistance = this.rotationConfig.normalDistance; // Track starting zoom distance
        this.overlayUpdateTimeout = null; // Debounce overlay updates
        this.overlayUpdateFrameCounter = 0; // Frame counter for throttling overlay updates during rotation
        
        // Performance optimization flags
        this.isDestroying = false;
        this.frameId = null;
        
        // Auto-rotation state (device-specific)
        this.autoRotationActive = false;
        this.autoRotationAngle = 270; // Current auto-rotation angle (start at About Us - 270°)
        this.autoRotationDistance = this.deviceConfig.optimalDistance; // Device-specific stable distance for auto-rotation
        this.lastUserInteractionTime = 0;
        this.resumeAutoRotationTimer = null;
        this.autoRotationUpdating = false; // Flag to prevent interaction detection during auto-rotation updates
        
        // Enhanced camera state management
        this.savedCameraState = {
            position: { x: 0, y: 0, z: 0 },
            target: { x: 0, y: 1, z: 0 },
            distance: this.deviceConfig.optimalDistance,
            angle: 270,
            timestamp: 0
        };
        this.userInteracting = false;
        this.interactionStartTime = 0;
        this.smoothTransitionActive = false;
        this.transitionProgress = 0;
        this.transitionDuration = this.performanceConfig.transitionDuration;
        
        // Timer management system
        this.activeTimers = new Set();
        this.activeIntervals = new Set();
        
        // Ground level constraint system
        this.groundConstraints = {
            minHeight: 1, // Minimum camera height above ground
            maxHeight: 30, // Maximum camera height above ground
            maxUndergroundLookAngle: 0, // Maximum upward look component (0.7 ≈ 45°)
            constraintBuffer: 0.5 // Safety buffer for target-relative positioning
        };
        
        // Camera animation properties
        this.startCameraState = {
            position: { x: 0, y: 0, z: 0 },
            target: { x: 0, y: 0, z: 0 }
        };
        this.targetCameraState = {
            position: { x: 0, y: 0, z: 0 },
            target: { x: 0, y: 0, z: 0 }
        };
        
        // Define camera presets for Y-axis rotation (device-specific distances)
        this.cameraPresets = {
            180: { // Contact Us - Front view
                distance: this.deviceConfig.optimalDistance,
                height: this.deviceConfig.cameraHeight,
                target: { x: 0, y: 1, z: 0 }
            },
            90: { // Our Projects - Right side
                distance: this.deviceConfig.optimalDistance,
                height: this.deviceConfig.cameraHeight,
                target: { x: 0, y: 1, z: 0 }
            },
            0: { // Our Services - Back view
                distance: this.deviceConfig.optimalDistance,
                height: this.deviceConfig.cameraHeight,
                target: { x: 0, y: 1, z: 0 }
            },
            270: { // About Us - Left side
                distance: this.deviceConfig.optimalDistance,
                height: this.deviceConfig.cameraHeight,
                target: { x: 0, y: 1, z: 0 }
            }
        };
        
        this.initializeWithProgress();
    }

    // Device detection and configuration methods
    detectDeviceType() {
        const userAgent = navigator.userAgent.toLowerCase();
        const width = window.innerWidth;
        const height = window.innerHeight;
        const pixelRatio = window.devicePixelRatio || 1;
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        
        // Check for tablet indicators
        const isTablet = (
            isTouchDevice && 
            ((width >= 768 && width <= 1024) || (height >= 768 && height <= 1024)) &&
            !/mobi|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
        ) || /ipad|tablet|kindle|silk/i.test(userAgent);
        
        // Check for mobile indicators
        const isMobile = (
            isTouchDevice && 
            (width < 768 || height < 768) &&
            /mobi|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
        ) && !isTablet;
        
        if (isMobile) return 'mobile';
        if (isTablet) return 'tablet';
        return 'desktop';
    }
    
    getDeviceConfiguration() {
        const configurations = {
            mobile: {
                minDistance: 60,
                maxDistance: 90,
                optimalDistance:80,
                zoomSpeed: 1.2,
                cameraHeight: 12,
                fov: 85,
                description: 'Mobile - Closer view with wider FOV for better visibility on small screens'
            },
            tablet: {
                minDistance: 60,
                maxDistance: 90,
                optimalDistance: 80,
                zoomSpeed: 1.0,
                cameraHeight: 20,
                fov: 80,
                description: 'Tablet - Balanced view for medium-sized screens'
            },
            desktop: {
                minDistance: 60,
                maxDistance: 90,
                optimalDistance:80,
                zoomSpeed: 0.8,
                cameraHeight: 15,
                fov: 75,
                description: 'Desktop - Standard view for large screens with precise controls'
            }
        };
        
        const config = configurations[this.deviceType];
        
        return config;
    }
    
    // Update device configuration on window resize
    updateDeviceConfiguration() {
        const newDeviceType = this.detectDeviceType();
        
        if (newDeviceType !== this.deviceType) {
            this.deviceType = newDeviceType;
            this.deviceConfig = this.getDeviceConfiguration();
            
            // Update zoom limits
            this.zoomLimits.minDistance = this.deviceConfig.minDistance;
            this.zoomLimits.maxDistance = this.deviceConfig.maxDistance;
            this.zoomLimits.optimalDistance = this.deviceConfig.optimalDistance;
            this.zoomLimits.zoomSpeed = this.deviceConfig.zoomSpeed;
            
            // Update controls if they exist
            if (this.controls) {
                this.controls.minDistance = this.deviceConfig.minDistance;
                this.controls.maxDistance = this.deviceConfig.maxDistance;
                this.controls.zoomSpeed = this.deviceConfig.zoomSpeed;
                
                // Update device-specific control optimizations
                if (this.deviceType === 'mobile') {
                    this.controls.touchDollyPan = true;
                    this.controls.dollySpeed = 1.5;
                    this.controls.rotateSpeed = 1.2;
                } else if (this.deviceType === 'tablet') {
                    this.controls.touchDollyPan = true;
                    this.controls.dollySpeed = 1.3;
                    this.controls.rotateSpeed = 1.1;
                } else {
                    this.controls.touchDollyPan = false;
                    this.controls.dollySpeed = 1.0;
                    this.controls.rotateSpeed = 1.0;
                }
                
                // Update camera FOV
                this.camera.fov = this.deviceConfig.fov;
                this.camera.updateProjectionMatrix();
            }
            
            // Update rotation and camera presets
            this.rotationConfig.normalDistance = this.deviceConfig.optimalDistance;
            this.rotationConfig.zoomInDistance = this.deviceConfig.optimalDistance;
            
            // Update camera presets
            Object.keys(this.cameraPresets).forEach(angle => {
                this.cameraPresets[angle].distance = this.deviceConfig.optimalDistance;
                this.cameraPresets[angle].height = this.deviceConfig.cameraHeight;
            });
            
            // Update auto-rotation distance if active
            if (this.autoRotationActive) {
                this.autoRotationDistance = this.deviceConfig.optimalDistance;
            }
            
            return true; // Configuration was updated
        }
        
        return false; // No change
    }

    init() {
        // Scene setup
        this.scene = new THREE.Scene();
        // Remove fog and background as they will be handled by sky and water
        // this.scene.fog = new THREE.Fog(0x87CEEB, 10, 100); // Sky blue fog
        // this.scene.background = new THREE.Color(0x87CEEB); // Sky blue background

        // Camera setup (device-specific FOV and position)
        const container = document.getElementById('container');
        const aspect = window.innerWidth / window.innerHeight;
        this.camera = new THREE.PerspectiveCamera(this.deviceConfig.fov, aspect, 0.1, 1000);
        
        // Start at About Us (270°) with device-specific distance and height
        const startDistance = this.deviceConfig.optimalDistance;
        const startHeight = Math.max(this.groundConstraints.minHeight, this.deviceConfig.cameraHeight); // Ensure minimum height above ground
        const startAngle = 270 * (Math.PI / 180); // 270° in radians
        this.camera.position.set(
            startDistance * Math.sin(startAngle),  // x = distance * sin(270°) = -distance
            startHeight,                           // y = device-specific height (minimum 1.0)
            startDistance * Math.cos(startAngle)   // z = distance * cos(270°) = 0
        );
        
        // Set camera to look at the center of the scene
        this.camera.lookAt(0, 0, 0);
        
        // Apply initial ground constraints
        this.enforceGroundConstraints();

        // Renderer setup
        this.renderer = new THREE.WebGLRenderer({
            canvas: document.getElementById('three-canvas'),
            antialias: true
        });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.6; // Enhanced exposure for more vibrant colors

        // Handle window resize with passive listener
        this.onWindowResize = this.onWindowResize.bind(this);
        window.addEventListener('resize', this.onWindowResize, { passive: true });
    }

    setupLighting() {
        
        // Enhanced ambient light for vibrancy
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6); // Increased for more vibrancy
        this.scene.add(ambientLight);

        // Bright main directional light
        const mainDirectionalLight = new THREE.DirectionalLight(0xffdd66, 3); // Increased for vibrancy
        mainDirectionalLight.position.set(100, 120, 80);
        mainDirectionalLight.castShadow = true;
        mainDirectionalLight.shadow.mapSize.width = 2048;
        mainDirectionalLight.shadow.mapSize.height = 2048;
        mainDirectionalLight.shadow.camera.near = 0.5;
        mainDirectionalLight.shadow.camera.far = 500;
        mainDirectionalLight.shadow.camera.left = -100;
        mainDirectionalLight.shadow.camera.right = 100;
        mainDirectionalLight.shadow.camera.top = 100;
        mainDirectionalLight.shadow.camera.bottom = -100;
        mainDirectionalLight.shadow.bias = -0.0001; // Reduce shadow acne
        this.scene.add(mainDirectionalLight);

        // Brighter fill light for detail
        const fillLight = new THREE.DirectionalLight(0xffffff, 0.9); // Increased for shadow detail
        fillLight.position.set(-80, 100, -60);
        fillLight.castShadow = false; // No shadows to avoid conflicts
        // this.scene.add(fillLight);

        // Enhanced hemisphere light with green-boosted ground reflection
        const hemiLight = new THREE.HemisphereLight(0x87CEEB, 0x44ff44, 1.0); // Sky blue to vibrant green
        this.scene.add(hemiLight);

        // More vibrant colored accent lights
        const pointLight1 = new THREE.PointLight(0xff6666, 0.6, 120); // More saturated red
        pointLight1.position.set(40, 30, 40);
        this.scene.add(pointLight1);

        const pointLight2 = new THREE.PointLight(0x66aaff, 0.6, 120); // More saturated blue
        pointLight2.position.set(-40, 30, -40);
        this.scene.add(pointLight2);

        const pointLight3 = new THREE.PointLight(0xffdd66, 1, 100); // More saturated yellow
        pointLight3.position.set(0, 35, 50);
        this.scene.add(pointLight3);

        // Dedicated green lights for vegetation vibrancy
        const greenLight1 = new THREE.PointLight(0x44ff44, 1.2, 80); // Bright green for vegetation
        greenLight1.position.set(-20, 25, 20);
        this.scene.add(greenLight1);

        const greenLight2 = new THREE.PointLight(0x66ff33, 0.8, 100); // Lime green accent
        greenLight2.position.set(25, 20, -25);
        this.scene.add(greenLight2);

        // Very subtle rim lighting
        const rimLight1 = new THREE.DirectionalLight(0xffffff, 0.2); // Much reduced
        rimLight1.position.set(-150, 50, 100);
        this.scene.add(rimLight1);

        const rimLight2 = new THREE.DirectionalLight(0xffffff, 0.2); // Much reduced
        rimLight2.position.set(150, 50, -100);
        // this.scene.add(rimLight2);

    }

    setupEnvironment() {
        // Ground plane and grid helper removed to avoid square ground display

        // Load GLTF model
        this.loadCityModel();
    }

    setupSkyWaterScene() {
        // Initialize sky and water scene
        this.skyWaterScene = new SkyWaterScene(this.scene, this.renderer);
    }

    setupSeagullFlock() {
        // Double-check Android detection as additional safety
        const userAgent = navigator.userAgent.toLowerCase();
        const isAndroid = userAgent.includes('android');
        
        if (isAndroid) {
            console.log('🚫 ANDROID DETECTION: Seagulls force-disabled');
            this.seagullFlock = null;
            return;
        }
        
        // Only initialize seagull flocking on capable devices
        if (this.shouldEnableSeagulls()) {
            try {
                this.seagullFlock = new SeagullFlock(this.scene, this.renderer);
                console.log('✅ Seagull flock enabled for this device');
            } catch (error) {
                console.warn('⚠️ Failed to initialize seagull flock:', error);
                this.seagullFlock = null;
            }
        } else {
            console.log('📱 Seagull flock disabled for mobile/low-performance devices');
            this.seagullFlock = null;
        }
    }

    shouldEnableSeagulls() {
        // Detect device capabilities and decide whether to enable seagulls
        const userAgent = navigator.userAgent.toLowerCase();
        const isAndroid = userAgent.includes('android');
        const isIOS = userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod');
        const isMobile = isAndroid || isIOS || /mobile|tablet|phone/.test(userAgent);
        
        // STRICT: Always disable on Android - no exceptions
        if (isAndroid) {
            console.log('🚫 Android detected - seagulls disabled for performance');
            return false;
        }
        
        // STRICT: Always disable on iOS - no exceptions  
        if (isIOS) {
            console.log('🚫 iOS detected - seagulls disabled for performance');
            return false;
        }
        
        // STRICT: Always disable on any mobile device
        if (isMobile) {
            console.log('🚫 Mobile device detected - seagulls disabled for performance');
            return false;
        }
        
        // Check for performance indicators on desktop
        const hasWebGL2 = !!window.WebGL2RenderingContext;
        const memoryInfo = navigator.deviceMemory || 0; // GB of RAM (if available)
        const hardwareConcurrency = navigator.hardwareConcurrency || 1; // CPU cores
        
        // Desktop devices - enable seagulls with basic checks
        if (hasWebGL2 && hardwareConcurrency >= 2) {
            console.log('✅ Desktop device with sufficient specs - seagulls enabled');
            return true;
        }
        
        // Default: disable for any uncertain cases
        console.log('🚫 Uncertain device specs - seagulls disabled for safety');
        return false;
    }

    async setupAudioSystem() {
        try {
            // Initialize audio system
            this.audioSystem = new AudioSystem();
            const success = await this.audioSystem.init();
            
            if (success) {
                // Set up sound toggle button event listener
                this.setupSoundToggle();
                
                // Set up sound confirmation popup
                this.setupSoundConfirmationPopup();
                
                log.debug('Audio system setup completed');
            } else {
                log.warn('Audio system initialization failed');
            }
        } catch (error) {
            log.error('Error setting up audio system:', error);
        }
    }

    setupSoundToggle() {
        const soundToggle = document.getElementById('sound-toggle');
        if (!soundToggle) return;

        soundToggle.addEventListener('click', async () => {
            if (this.audioSystem) {
                if (this.audioSystem.isEnabled) {
                    this.audioSystem.toggleMute();
                } else {
                    await this.audioSystem.enableAudio();
                }
            }
        });

        // Show sound toggle button after model is loaded
        const showSoundToggle = () => {
            const container = document.querySelector('.sound-toggle-container-bottom');
            if (container) {
                container.classList.add('model-loaded');
            }
        };

        // Show sound toggle when loading is complete
        this.createManagedTimeout(showSoundToggle, 2000);
    }

    setupSoundConfirmationPopup() {
        const popup = document.getElementById('sound-confirmation-popup');
        const enableBtn = document.getElementById('enable-sound');
        const disableBtn = document.getElementById('disable-sound');
        const closeBtn = document.getElementById('close-sound-popup');

        if (!popup || !enableBtn || !disableBtn || !closeBtn) return;

        // Check if we should show the popup based on user preference
        if (this.audioSystem && !this.audioSystem.shouldShowPopup()) {
            // User has already made a choice, apply their preference
            if (this.audioSystem.userPreference === 'enabled') {
                // Enable audio after user interaction (browser requirement)
                this.audioSystem.pendingEnable = true;
            }
            return; // Don't show popup
        }

        // Show popup after a short delay only if user hasn't made a choice
        this.createManagedTimeout(() => {
            popup.style.display = 'flex';
            this.createManagedTimeout(() => {
                popup.classList.add('show');
            }, 100);
        }, 1000);

        // Enable sound button
        enableBtn.addEventListener('click', async () => {
            if (this.audioSystem) {
                await this.audioSystem.enableAudio();
            }
            this.hideSoundPopup();
        });

        // Disable sound button
        disableBtn.addEventListener('click', () => {
            if (this.audioSystem) {
                this.audioSystem.saveUserPreference('disabled');
            }
            this.hideSoundPopup();
        });

        // Close button
        closeBtn.addEventListener('click', () => {
            if (this.audioSystem) {
                this.audioSystem.saveUserPreference('disabled');
            }
            this.hideSoundPopup();
        });

        // Close on background click
        popup.addEventListener('click', (e) => {
            if (e.target === popup) {
                if (this.audioSystem) {
                    this.audioSystem.saveUserPreference('disabled');
                }
                this.hideSoundPopup();
            }
        });
    }

    hideSoundPopup() {
        const popup = document.getElementById('sound-confirmation-popup');
        if (popup) {
            popup.classList.remove('show');
            this.createManagedTimeout(() => {
                popup.style.display = 'none';
            }, 300);
        }
    }


    loadCityModel() {
        // Set up DRACOLoader for compressed models
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
        
        // Set up GLTFLoader with DRACOLoader
        const loader = new GLTFLoader();
        loader.setDRACOLoader(dracoLoader);
        
        // You can replace this path with your actual city model
        // Try different model files available:
        const modelPath = './models/island/island.glb'; // Main island model
        
        loader.load(
            modelPath,
            (gltf) => {
                // GLTF file loaded successfully
                
                // Analyze meshes and materials
                let meshCount = 0;
                let materialCount = 0;
                let textureCount = 0;
                
                gltf.scene.traverse((child) => {
                    if (child.isMesh) {
                        meshCount++;
                        
                        // Check material properties
                        if (child.material) {
                            materialCount++;
                            
                            if (child.material.map) {
                                textureCount++;
                            }
                        }
                    }
                });
                

                
                // Remove placeholder city
                // const placeholderCity = this.scene.getObjectByName('placeholderCity');
                // if (placeholderCity) {
                //     this.scene.remove(placeholderCity);
                // }
                
                // Add the loaded model
                this.cityModel = gltf.scene;
                this.cityModel.position.set(0, -3, 0); // Position above water surface
                
                // Ensure the model is centered by calculating its bounding box
                const centerBox = new THREE.Box3().setFromObject(this.cityModel);
                const center = centerBox.getCenter(new THREE.Vector3());
                
                // If the model's internal geometry isn't centered, adjust its position
                if (Math.abs(center.x) > 0.1 || Math.abs(center.z) > 0.1) {
                    this.cityModel.position.x -= center.x;
                    this.cityModel.position.z -= center.z;
                }
                
                // Calculate and log model dimensions before scaling
                const box = new THREE.Box3().setFromObject(this.cityModel);
                const size = box.getSize(new THREE.Vector3());
                
                if (size.x === 0 && size.y === 0 && size.z === 0) {
                    log.error('❌ Model has zero dimensions! This indicates the model data is empty or invalid.');
                }
                
                // Scale the model to appropriate size (adjust if needed)
                this.cityModel.scale.set(50, 50, 50);
                
                // Enable shadows for all meshes in the model
                this.cityModel.traverse((child) => {
                    if (child.isMesh) {
                        child.castShadow = true;
                        child.receiveShadow = true;
                    }
                });
                
                this.scene.add(this.cityModel);
                
                // Adjust zoom limits based on model size
                this.adjustZoomLimitsForModel();
                
                // Mark model loading as complete
                this.loadingProgress.modelLoadingActive = false;
            },
            (progress) => {
                // Update loading progress for model loading
                if (progress.total > 0) {
                    this.loadingProgress.modelLoadingActive = true;
                    const modelProgress = (progress.loaded / progress.total) * 100;
                    
                    // Calculate progress within the model loading stage (73-89%)
                    const stageStartPercent = 73; // Stage 7 starts at 73%
                    const stageEndPercent = 89;   // Stage 7 ends at 89%
                    const stageProgress = (modelProgress / 100) * (stageEndPercent - stageStartPercent);
                    const totalProgress = Math.min(stageStartPercent + stageProgress, stageEndPercent);
                    
                    this.updateLoadingProgress(7, `Loading Chromatic One Island... ${Math.round(modelProgress)}%`, totalProgress);
                }
            },
            (error) => {
                console.error('❌ Failed to load Chromatic Island model:', error);
                this.loadingProgress.modelLoadingActive = false;
            }
        );
    }

    adjustZoomLimitsForModel() {
        if (!this.cityModel) return;
        
        // Check if controls are initialized before trying to access them
        if (!this.controls) {
            return;
        }
        
        // Calculate model bounds
        const box = new THREE.Box3().setFromObject(this.cityModel);
        const size = box.getSize(new THREE.Vector3());
        const maxDimension = Math.max(size.x, size.y, size.z);
        
        // Adjust zoom limits based on model size if it's larger than expected
        if (maxDimension > 80) {
            const oldMaxDistance = this.zoomLimits.maxDistance;
            this.zoomLimits.maxDistance = Math.min(maxDimension * 2.5, 300); // Cap at 300
            this.controls.maxDistance = this.zoomLimits.maxDistance;
        }
        
        // Ensure optimal distance is reasonable for the model
        if (maxDimension > 100) {
            this.zoomLimits.optimalDistance = Math.min(maxDimension * 1.2, 150);
        }
    }

    setupControls() {
        // OrbitControls for mouse interaction
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.screenSpacePanning = false;
        this.controls.enablePan = false; // Disable panning - only zoom and rotate allowed
        
        // Set controls target to center of scene
        this.controls.target.set(0, 0, 0);
        
        // Track user distance changes
        this.lastUserDistance = null;
        this.controls.addEventListener('change', () => {
            if (!this.isRotating && !this.autoRotationActive && !this.smoothTransitionActive) {
                // User is manually controlling the camera - save their distance preference
                const currentDistance = Math.sqrt(
                    Math.pow(this.camera.position.x - this.controls.target.x, 2) + 
                    Math.pow(this.camera.position.z - this.controls.target.z, 2)
                );
                this.lastUserDistance = currentDistance;
            }
        });
        
        // Apply zoom limits configuration
        this.controls.minDistance = this.zoomLimits.minDistance;
        this.controls.maxDistance = this.zoomLimits.maxDistance;
        this.controls.zoomSpeed = this.zoomLimits.zoomSpeed;
        
        // Enhanced ground level constraints - more restrictive to prevent underground viewing
        this.controls.maxPolarAngle = Math.PI / 2.5; // More restrictive - prevents camera from going below ground
        this.controls.minPolarAngle = 0.8; // Slightly more restrictive for top view
        
        this.controls.addEventListener('change', () => {
            // Apply centralized ground constraints
            this.enforceGroundConstraints();
            
            // Save the current angle when user manually rotates the camera
            if (this.userInteracting && !this.autoRotationUpdating) {
                const currentAngle = this.getCurrentCameraAngle();
                // Only update if the angle has changed significantly (more than 5 degrees)
                if (Math.abs(currentAngle - this.autoRotationAngle) > 5) {
                    this.autoRotationAngle = currentAngle;
                }
            }
        });
        
        // Device-specific control optimizations
        if (this.deviceType === 'mobile') {
            this.controls.touchDollyPan = true; // Enable pan on double-finger touch
            this.controls.dollySpeed = 1.5; // Faster zoom on mobile for easier control
            this.controls.rotateSpeed = 1.2; // Slightly faster rotation for touch
        } else if (this.deviceType === 'tablet') {
            this.controls.touchDollyPan = true; // Enable pan on double-finger touch
            this.controls.dollySpeed = 1.3; // Balanced zoom speed for tablet
            this.controls.rotateSpeed = 1.1; // Slightly faster rotation for touch
        } else {
            // Desktop - keep default speeds
            this.controls.touchDollyPan = false; // Disable for mouse users
        }
        
        // Set target to look at the center of the city (slightly elevated)
        this.controls.target.set(0, 1, 0);
        
        // Enhanced zoom monitoring with stabilization
        let lastZoomUpdateTime = 0;
        let stableZoomDistance = this.deviceConfig.optimalDistance;
        
        this.controls.addEventListener('change', () => {
            const currentTime = performance.now();
            const distance = this.camera.position.distanceTo(this.controls.target);
            
            // Zoom stabilization - only update if change is significant and sufficient time has passed
            if (currentTime - lastZoomUpdateTime > 100) { // Minimum 100ms between updates
                const xzDistance = Math.sqrt(
                    Math.pow(this.camera.position.x - this.controls.target.x, 2) + 
                    Math.pow(this.camera.position.z - this.controls.target.z, 2)
                );
                
                // Only update if there's a significant change to prevent micro-adjustments
                const distanceDiff = Math.abs(xzDistance - stableZoomDistance);
                if (distanceDiff > 0.8) { // Increased threshold for stability
                    stableZoomDistance = xzDistance;
                    lastZoomUpdateTime = currentTime;
                    
                    // Update auto-rotation distance only if auto-rotation is active and user is not in transition
                    if (this.autoRotationActive && !this.isRotating && !this.smoothTransitionActive) {
                        // Only update if the change is significant to prevent micro-adjustments
                        const autoRotationDiff = Math.abs(xzDistance - this.autoRotationDistance);
                        if (autoRotationDiff > 2.0) { // Higher threshold to prevent jerking
                            this.autoRotationDistance = xzDistance; // Direct assignment for stability
                        }
                    }
                }
            }
            
            // Height constraint enforcement - prevent camera from going below ground
            this.enforceGroundConstraints();
        });
        
        // Add event listeners for manual control interruption
        this.setupRotationInterruption();
    }


    setupNavigation() {
        // Logo click to go to Contact Us (0°)
        const logo = document.querySelector('.logo');
        logo.addEventListener('click', (event) => {
            event.preventDefault();
            this.rotateCameraToAngle(0);
            
            // Clear all active navigation states
            const navLinks = document.querySelectorAll('.nav-menu a[data-angle]');
            navLinks.forEach(l => l.classList.remove('active'));
        });

        // Hamburger menu toggle
        const hamburger = document.getElementById('hamburger');
        const navMenu = document.getElementById('nav-menu');
        
        hamburger.addEventListener('click', () => {
            hamburger.classList.toggle('active');
            navMenu.classList.toggle('active');
        });

        // Close menu when clicking outside
        document.addEventListener('click', (event) => {
            if (!hamburger.contains(event.target) && !navMenu.contains(event.target)) {
                hamburger.classList.remove('active');
                navMenu.classList.remove('active');
            }
        });

        // Navigation link functionality
        const navLinks = document.querySelectorAll('.nav-menu a[data-angle]');
        navLinks.forEach(link => {
            link.addEventListener('click', (event) => {
                event.preventDefault();
                
                const angle = parseInt(link.getAttribute('data-angle'));
                this.rotateCameraToAngle(angle);
                
                // Close mobile menu after clicking
                hamburger.classList.remove('active');
                navMenu.classList.remove('active');
                
                // Update active link styling
                navLinks.forEach(l => l.classList.remove('active'));
                link.classList.add('active');
            });
        });
    }

    /**
     * Smoothly rotates camera to specified angle
     * @param {number} targetAngleDegrees - Target angle in degrees (0-360)
     */
    rotateCameraToAngle(targetAngleDegrees) {
        // Stop any current rotation and clear queue
        this.stopRotation();
        
        // Calculate sequential rotation path
        this.rotationQueue = this.calculateRotationPath(this.currentAngle, targetAngleDegrees);
        
        if (this.rotationQueue.length > 0) {
            this.startNextRotation();
        }
    }
    
    saveCurrentAngleForAutoRotation(angle) {
        // Save the current angle as the new starting point for auto-rotation
        this.autoRotationAngle = angle;
        this.currentAngle = angle;
        
        // Update the auto-rotation sequence to start from this angle
        this.updateAutoRotationSequenceFromAngle(angle);
        
        // Save camera state for smooth transitions
        this.saveCameraState();
        
        // console.log(`🔄 Auto-rotation will continue from angle: ${angle}°`);
    }
    
    updateAutoRotationSequenceFromAngle(startAngle) {
        // Create a new sequence starting from the current angle
        const baseSequence = [270, 0, 90, 180]; // Original sequence: About Us → Our Services → Our Projects → Contact Us
        
        // Find the index of the closest angle in the base sequence
        let closestIndex = 0;
        let minDifference = 360;
        
        for (let i = 0; i < baseSequence.length; i++) {
            const difference = Math.abs(baseSequence[i] - startAngle);
            const wrappedDifference = Math.min(difference, 360 - difference);
            
            if (wrappedDifference < minDifference) {
                minDifference = wrappedDifference;
                closestIndex = i;
            }
        }
        
        // Create a new sequence starting from the closest angle
        const newSequence = [];
        for (let i = 0; i < baseSequence.length; i++) {
            const index = (closestIndex + i) % baseSequence.length;
            newSequence.push(baseSequence[index]);
        }
        
        // Update the auto-rotation configuration
        this.autoRotationConfig.sequence = newSequence;
        this.autoRotationConfig.sequenceIndex = 0; // Start from the beginning of the new sequence
        
        // console.log(`🔄 Updated auto-rotation sequence: [${newSequence.join(', ')}] starting from ${startAngle}°`);
    }
    
    stopRotation() {
        if (this.isRotating) {
            this.isRotating = false;
            this.rotationQueue = [];
            this.controls.enabled = true;
            this.overlayUpdateFrameCounter = 0; // Reset frame counter
            
            // Update current angle to where we stopped
            const currentCameraAngle = Math.atan2(
                this.camera.position.x,
                this.camera.position.z
            );
            this.currentAngle = Math.round((currentCameraAngle * 180 / Math.PI + 360) % 360 / 90) * 90;
            
            // Update overlay when angle is manually calculated (only if loading is complete)
            if (this.overlays && this.loadingComplete) {
                this.scheduleOverlayUpdate(this.currentAngle);
            }
        }
    }
    
    calculateRotationPath(currentAngle, targetAngle) {
        const path = [];
        
        // Calculate the shortest angular distance
        let diff = (targetAngle - currentAngle + 360) % 360;
        
        // If more than 90 degrees apart, go directly
        if (diff > 90) {
            path.push(targetAngle);
        } else if (diff > 0) {
            // Only step through if exactly 90 degrees apart
            let current = currentAngle;
            while (current !== targetAngle) {
                current = (current + 90) % 360;
                path.push(current);
            }
        }
        // If diff is 0, we're already at target angle
        
        return path;
    }
    
    startNextRotation() {
        if (this.rotationQueue.length === 0) {
            this.controls.enabled = true;
            // Save the final angle for auto-rotation continuation when all rotations are complete
            this.saveCurrentAngleForAutoRotation(this.currentAngle);
            return;
        }
        
        const nextAngle = this.rotationQueue.shift();
        const preset = this.cameraPresets[nextAngle];
        if (!preset) return;
        
        // Store current camera state and distance
        // Use optimal distance for consistent navigation experience (same as auto-rotation)
        const currentDistance = this.deviceConfig.optimalDistance;
        this.startDistance = currentDistance;
        
        // Store camera state with safety check
        const targetPosition = (this.controls && this.controls.target) ? 
            { x: this.controls.target.x, y: this.controls.target.y, z: this.controls.target.z } :
            { x: 0, y: 1, z: 0 }; // Default target position
        
        this.startCameraState = {
            position: {
                x: this.camera.position.x,
                y: this.camera.position.y,
                z: this.camera.position.z
            },
            target: targetPosition
        };
        
        // Calculate target position using Y-axis rotation with CURRENT zoom distance
        const azimuthRadians = (nextAngle * Math.PI) / 180;
        const newPosition = {
            x: preset.target.x + currentDistance * Math.sin(azimuthRadians), // Use currentDistance instead of preset.distance
            y: preset.height,
            z: preset.target.z + currentDistance * Math.cos(azimuthRadians)  // Use currentDistance instead of preset.distance
        };
        
        // Set target camera state
        this.targetCameraState = {
            position: newPosition,
            target: preset.target
        };
        
        // Start rotation animation
        this.isRotating = true;
        this.rotationProgress = 0;
        this.rotationStartTime = performance.now();
        this.targetAngle = nextAngle;
        this.overlayUpdateFrameCounter = 0; // Reset frame counter for smooth overlay updates
        
        // Disable user controls during rotation
        this.controls.enabled = false;
    }

    showRotationFeedback(angle) {
        // Create a temporary feedback element
        const feedback = document.createElement('div');
        feedback.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 15px 25px;
            border-radius: 10px;
            font-size: 18px;
            z-index: 2000;
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.2);
            transition: opacity 0.3s ease;
            text-align: center;
        `;
        
        // Get section name and description
        const sections = {
            0: { name: 'Contact Us', description: `Rotating to front view (${this.rotationConfig.duration/1000}s)` },
            90: { name: 'Our Projects', description: `Rotating to right side (${this.rotationConfig.duration/1000}s)` },
            180: { name: 'Our Services', description: `Rotating to back view (${this.rotationConfig.duration/1000}s)` },
            270: { name: 'About Us', description: `Rotating to left side (${this.rotationConfig.duration/1000}s)` }
        };
        
        const section = sections[angle] || { name: 'View', description: 'Camera movement' };
        
        // Secure DOM manipulation - prevents XSS attacks
        // Create title element with secure text content
        const titleDiv = document.createElement('div');
        titleDiv.textContent = section.name; // Safe: uses textContent instead of innerHTML
        titleDiv.style.fontWeight = 'bold';
        titleDiv.style.marginBottom = '5px';
        
        // Create description element with secure text content
        const descDiv = document.createElement('div');
        descDiv.textContent = section.description; // Safe: uses textContent instead of innerHTML
        descDiv.style.fontSize = '14px';
        descDiv.style.opacity = '0.8';
        
        // Append elements to feedback container
        feedback.appendChild(titleDiv);
        feedback.appendChild(descDiv);
        document.body.appendChild(feedback);
        
        // Remove feedback after animation (adjusted for 3-second duration)
        setTimeout(() => {
            feedback.style.opacity = '0';
            setTimeout(() => {
                if (feedback.parentNode) {
                    feedback.parentNode.removeChild(feedback);
                }
            }, 300);
        }, 2500);
    }

    // Easing function for smooth animation (ease-in-out cubic)
    easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    updateCameraRotation() {
        if (!this.isRotating) return;
        
        // Temporarily disable controls to prevent interference
        const controlsWereEnabled = this.controls.enabled;
        this.controls.enabled = false;
        
        const currentTime = performance.now();
        const elapsed = currentTime - this.rotationStartTime;
        this.rotationProgress = Math.min(elapsed / this.rotationConfig.duration, 1);
        
        // Apply easing to the progress for cinematic movement
        const easedProgress = this.easeInOutCubic(this.rotationProgress);
        
        // Safety check for controls during rotation updates
        if (!this.controls || !this.controls.target) {
            return;
        }
        
        // Pure Y-axis rotation calculation (orbital movement around Y axis)
        const startAngle = Math.atan2(
            this.startCameraState.position.x - this.controls.target.x,
            this.startCameraState.position.z - this.controls.target.z
        );
        const endAngle = Math.atan2(
            this.targetCameraState.position.x - this.controls.target.x,
            this.targetCameraState.position.z - this.controls.target.z
        );
        
        // Interpolate the angle around Y-axis only
        const currentAngle = this.lerpAngle(startAngle, endAngle, easedProgress);
        
        // FIXED: Maintain current zoom level during navigation (no zoom-in effect)
        let distance = this.startDistance;
        
        // OLD CODE (causing unwanted zoom during navigation):
        // if (easedProgress < 0.5) {
        //     // First half: zoom in from start distance to close distance
        //     const zoomProgress = easedProgress * 2; // 0 to 1 over first half
        //     distance = this.startDistance + (this.rotationConfig.zoomInDistance - this.startDistance) * zoomProgress;
        // } else {
        //     // Second half: zoom out from close distance to target distance
        //     const zoomProgress = (easedProgress - 0.5) * 2; // 0 to 1 over second half
        //     distance = this.rotationConfig.zoomInDistance + (this.rotationConfig.normalDistance - this.rotationConfig.zoomInDistance) * zoomProgress;
        // }
        
        // Calculate camera position using dynamic distance and Y-axis rotation (device-specific height)
        this.camera.position.x = this.controls.target.x + distance * Math.sin(currentAngle);
        this.camera.position.y = Math.max(this.groundConstraints.minHeight, this.deviceConfig.cameraHeight); // Ensure minimum height above ground
        this.camera.position.z = this.controls.target.z + distance * Math.cos(currentAngle);
        
        // Apply additional ground constraints
        this.enforceGroundConstraints();
        
        // Keep target fixed at city center
        this.controls.target.set(0, 1, 0);
        
        // Update camera to look at target (no Z-axis rotation/roll)
        this.camera.lookAt(this.controls.target);
        this.camera.up.set(0, 1, 0); // Ensure camera stays upright (no roll)
        
        // Check if animation is complete
        if (this.rotationProgress >= 1) {
            // Ensure final position is exactly at target
            this.camera.position.set(
                this.targetCameraState.position.x,
                this.targetCameraState.position.y,
                this.targetCameraState.position.z
            );
            this.controls.target.set(
                this.targetCameraState.target.x,
                this.targetCameraState.target.y,
                this.targetCameraState.target.z
            );
            this.camera.lookAt(this.controls.target);
            
            // Update current angle and continue with next rotation
            this.currentAngle = this.targetAngle;
            this.isRotating = false;
            this.overlayUpdateFrameCounter = 0; // Reset frame counter when rotation completes
            
            // Save the current angle for auto-rotation continuation when rotation completes
            this.saveCurrentAngleForAutoRotation(this.currentAngle);
            
            // Debounced overlay update to prevent flickering
            if (this.loadingComplete) {
                this.scheduleOverlayUpdate(this.currentAngle);
            }
            
            // Start next rotation if queue has more
            this.createManagedTimeout(() => {
                this.startNextRotation();
            }, 100); // Small delay between rotations
        }
        
        // Re-enable controls if they were enabled before rotation
        if (controlsWereEnabled) {
            this.controls.enabled = true;
        }
    }
    
    // Linear interpolation helper function
    /**
     * Linear interpolation between two values
     * @param {number} start - Starting value
     * @param {number} end - Ending value  
     * @param {number} progress - Progress from 0 to 1
     * @returns {number} Interpolated value
     */
    lerp(start, end, progress) {
        return start + (end - start) * progress;
    }
    
    // Timer management utilities
    createManagedTimeout(callback, delay) {
        const timeoutId = setTimeout(() => {
            this.activeTimers.delete(timeoutId);
            if (!this.isDestroying) {
                callback();
            }
        }, delay);
        this.activeTimers.add(timeoutId);
        return timeoutId;
    }
    
    createManagedInterval(callback, interval) {
        const intervalId = setInterval(() => {
            if (!this.isDestroying) {
                callback();
            }
        }, interval);
        this.activeIntervals.add(intervalId);
        return intervalId;
    }
    
    clearManagedTimer(timerId) {
        if (this.activeTimers.has(timerId)) {
            clearTimeout(timerId);
            this.activeTimers.delete(timerId);
        }
        if (this.activeIntervals.has(timerId)) {
            clearInterval(timerId);
            this.activeIntervals.delete(timerId);
        }
    }
    
    // Cleanup all managed timers
    destroy() {
        this.isDestroying = true;
        
        // Clear all managed timers
        this.activeTimers.forEach(timerId => clearTimeout(timerId));
        this.activeIntervals.forEach(intervalId => clearInterval(intervalId));
        this.activeTimers.clear();
        this.activeIntervals.clear();
        
        // Cancel animation frame
        if (this.frameId) {
            cancelAnimationFrame(this.frameId);
        }
        
        // Cleanup audio system
        if (this.audioSystem) {
            this.audioSystem.destroy?.();
        }
        
        // Cleanup event listeners
        window.removeEventListener('resize', this.onWindowResize);
    }
    
    // Angle interpolation helper function (handles angle wrapping)
    lerpAngle(start, end, progress) {
        // Calculate shortest path between angles
        let diff = end - start;
        while (diff > Math.PI) diff -= 2 * Math.PI;
        while (diff < -Math.PI) diff += 2 * Math.PI;
        
        return start + diff * progress;
    }
    
    setupRotationInterruption() {
        // Detect manual mouse/touch controls
        const canvas = this.renderer.domElement;
        
        // Mouse events with passive listeners where appropriate
        const handleMouseDown = () => {
            this.startUserInteraction();
            if (this.rotationConfig.interruptible && this.isRotating) {
                this.stopRotation();
            }
        };
        canvas.addEventListener('mousedown', handleMouseDown);
        
        const handleMouseUp = () => {
            this.endUserInteraction();
        };
        canvas.addEventListener('mouseup', handleMouseUp);
        
        // Touch events
        canvas.addEventListener('touchstart', () => {
            this.startUserInteraction();
            if (this.rotationConfig.interruptible && this.isRotating) {
                this.stopRotation();
            }
        });
        
        canvas.addEventListener('touchend', () => {
            this.endUserInteraction();
        });
        
        // Wheel events (zoom/scroll) with passive listener
        const handleWheel = () => {
            this.startUserInteraction();
            if (this.rotationConfig.interruptible && this.isRotating) {
                this.stopRotation();
            }
            // For wheel events, end interaction after a short delay
            this.createManagedTimeout(() => {
                this.endUserInteraction();
            }, 100);
        };
        canvas.addEventListener('wheel', handleWheel, { passive: true });
        
        // Keyboard events (arrow keys disabled for movement)
        document.addEventListener('keydown', (event) => {
            // Stop rotation on WASD keys only (arrow keys disabled)
            const interruptKeys = ['KeyA', 'KeyS', 'KeyD', 'KeyW'];
            if (interruptKeys.includes(event.code)) {
                this.startUserInteraction();
                if (this.rotationConfig.interruptible && this.isRotating) {
                    this.stopRotation();
                }
            }
        });
        
        document.addEventListener('keyup', (event) => {
            const interruptKeys = ['KeyA', 'KeyS', 'KeyD', 'KeyW'];
            if (interruptKeys.includes(event.code)) {
                this.endUserInteraction();
            }
        });
        
        // Also pause auto-rotation on navigation clicks
        const navLinks = document.querySelectorAll('.nav-menu a[data-angle]');
        navLinks.forEach(link => {
            link.addEventListener('click', () => {
                this.pauseAutoRotation();
            });
        });
        
        // Pause auto-rotation on logo click
        const logo = document.querySelector('.logo');
        if (logo) {
            logo.addEventListener('click', () => {
                this.pauseAutoRotation();
            });
        }
    }
    
    // Ground level constraint enforcement - centralized method
    enforceGroundConstraints() {
        // Safety check - only run if controls are initialized
        if (!this.controls || !this.controls.target) {
            // If controls aren't ready, just enforce basic height constraint
            if (this.camera.position.y < this.groundConstraints.minHeight) {
                this.camera.position.y = this.groundConstraints.minHeight;
                this.camera.updateMatrixWorld();
            }
            return false;
        }
        
        let constraintApplied = false;
        
        // Primary height constraint - never allow camera below ground
        if (this.camera.position.y < this.groundConstraints.minHeight) {
            this.camera.position.y = this.groundConstraints.minHeight;
            constraintApplied = true;
        }
        
        // Maximum height constraint - never allow camera above maximum height
        if (this.camera.position.y > this.groundConstraints.maxHeight) {
            this.camera.position.y = this.groundConstraints.maxHeight;
            constraintApplied = true;
        }
        
        // Secondary constraint - ensure camera isn't looking up from underground position
        const cameraToTarget = new THREE.Vector3().subVectors(this.controls.target, this.camera.position);
        const groundPlane = new THREE.Vector3(0, 1, 0); // Up vector representing ground normal
        
        // If camera is below target and looking up significantly, adjust camera height
        if (this.camera.position.y < this.controls.target.y - this.groundConstraints.constraintBuffer) {
            const lookDirection = cameraToTarget.normalize();
            const upComponent = lookDirection.dot(groundPlane);
            
            // If looking too much upward (suggesting underground position), force camera higher
            if (upComponent > this.groundConstraints.maxUndergroundLookAngle) {
                this.camera.position.y = Math.max(
                    this.controls.target.y - this.groundConstraints.constraintBuffer, 
                    this.groundConstraints.minHeight
                );
                constraintApplied = true;
            }
        }
        
        // Update camera matrix if constraints were applied
        if (constraintApplied) {
            this.camera.updateMatrixWorld();
        }
        
        return constraintApplied;
    }
    
    // Enhanced user interaction tracking methods
    startUserInteraction() {
        if (!this.userInteracting) {
            this.userInteracting = true;
            this.interactionStartTime = performance.now();
            this.pauseAutoRotation();
            
            // Hide drag indicator when user starts interacting
            this.hideDragIndicator();
        }
    }
    
    endUserInteraction() {
        if (this.userInteracting) {
            this.userInteracting = false;
            this.saveCameraState();
            
            // Save the current angle for auto-rotation continuation
            const currentAngle = this.getCurrentCameraAngle();
            this.saveCurrentAngleForAutoRotation(currentAngle);
        }
    }
    
    saveCameraState() {
        // Safety check - only save if controls are initialized
        if (!this.controls || !this.controls.target) {
            return;
        }
        
        // Save current camera position, target, and calculated distance
        const distance = Math.sqrt(
            Math.pow(this.camera.position.x - this.controls.target.x, 2) + 
            Math.pow(this.camera.position.z - this.controls.target.z, 2)
        );
        
        const angle = this.getCurrentCameraAngle();
        
        this.savedCameraState = {
            position: {
                x: this.camera.position.x,
                y: this.camera.position.y,
                z: this.camera.position.z
            },
            target: {
                x: this.controls.target.x,
                y: this.controls.target.y,
                z: this.controls.target.z
            },
            distance: distance,
            angle: angle,
            timestamp: performance.now()
        };
    }
    
    // Configuration methods
    setRotationDuration(duration) {
        this.rotationConfig.duration = duration;
    }
    
    getRotationDuration() {
        return this.rotationConfig.duration;
    }
    
    setRotationInterruptible(interruptible) {
        this.rotationConfig.interruptible = interruptible;
    }
    
    // Auto-rotation methods
    startAutoRotation() {
        if (!this.autoRotationConfig.enabled) return;
        
        this.autoRotationActive = true;
        // Initialize auto-rotation angle to current camera angle
        this.autoRotationAngle = this.getCurrentCameraAngle();
        // Use device-specific optimal distance for consistency
        this.autoRotationDistance = this.deviceConfig.optimalDistance;
    }
    
    stopAutoRotation() {
        this.autoRotationActive = false;
        
        if (this.resumeAutoRotationTimer) {
            clearTimeout(this.resumeAutoRotationTimer);
            this.resumeAutoRotationTimer = null;
        }
    }
    
    updateContinuousAutoRotation() {
        // Handle smooth transition first
        if (this.smoothTransitionActive) {
            this.updateSmoothTransition();
            return;
        }
        
        if (!this.autoRotationActive || !this.autoRotationConfig.enabled || this.isRotating) {
            this.autoRotationUpdating = false;
            return;
        }
        
        // Set flag to prevent interaction detection during auto-rotation updates
        this.autoRotationUpdating = true;
        
        // Safety check for controls during auto-rotation
        if (!this.controls || !this.controls.target) {
            return;
        }
        
        // Use actual frame time for smoother animation
        const now = performance.now();
        const deltaTime = Math.min((now - (this.lastAutoRotationTime || now)) / 1000, 1/30); // Cap at 30fps to prevent jumps
        this.lastAutoRotationTime = now;
        
        // Get current target angle from sequence
        const currentTargetAngle = this.autoRotationConfig.sequence[this.autoRotationConfig.sequenceIndex];
        
        // Calculate shortest path to target angle
        let angleDiff = currentTargetAngle - this.autoRotationAngle;
        if (angleDiff > 180) angleDiff -= 360;
        if (angleDiff < -180) angleDiff += 360;
        
        // Continuous rotation without pausing at target angles
        const rotationSpeed = this.autoRotationConfig.speed * deltaTime;
        
        // Always rotate continuously
        this.autoRotationAngle += rotationSpeed;
        
        // Keep angle in 0-360 range
        this.autoRotationAngle = ((this.autoRotationAngle % 360) + 360) % 360;
        
        // Update sequence index based on current angle for overlay system
        const currentAngleNormalized = Math.round(this.autoRotationAngle / 90) * 90;
        const targetAngle = this.autoRotationConfig.sequence[this.autoRotationConfig.sequenceIndex];
        
        // If we've passed the current target angle, move to next sequence
        if (Math.abs(this.autoRotationAngle - targetAngle) > 45) {
            // Check if we've moved past the current target angle
            let angleDiff = this.autoRotationAngle - targetAngle;
            if (angleDiff > 180) angleDiff -= 360;
            if (angleDiff < -180) angleDiff += 360;
            
            if (Math.abs(angleDiff) > 45) {
                // Move to next angle in sequence
                this.autoRotationConfig.sequenceIndex = (this.autoRotationConfig.sequenceIndex + 1) % this.autoRotationConfig.sequence.length;
            }
        }
        
        // Keep angle in 0-360 range
        this.autoRotationAngle = ((this.autoRotationAngle % 360) + 360) % 360;
        
        // Calculate camera position based on current auto-rotation angle
        const angleRadians = (this.autoRotationAngle * Math.PI) / 180;
        // Use stable stored distance instead of recalculating (prevents floating-point drift)
        const distance = this.autoRotationDistance;
        
        // Don't disable controls during auto-rotation to allow manual interaction
        // const controlsWereEnabled = this.controls.enabled;
        // this.controls.enabled = false;
        
        // Apply the continuous rotation to camera position (device-specific height)
        // Use smooth interpolation to prevent jerking
        const targetX = this.controls.target.x + distance * Math.sin(angleRadians);
        const targetY = Math.max(this.groundConstraints.minHeight, this.deviceConfig.cameraHeight);
        const targetZ = this.controls.target.z + distance * Math.cos(angleRadians);
        
        // Only update camera position if user is not manually interacting
        if (!this.userInteracting) {
            // Smooth interpolation to prevent jerking
            this.camera.position.x = this.lerp(this.camera.position.x, targetX, 0.1);
            this.camera.position.y = this.lerp(this.camera.position.y, targetY, 0.1);
            this.camera.position.z = this.lerp(this.camera.position.z, targetZ, 0.1);
        }
        
        // Apply additional ground constraints
        this.enforceGroundConstraints();
        
        // Update camera to look at target
        this.camera.lookAt(this.controls.target);
        this.camera.up.set(0, 1, 0); // Ensure camera stays upright
        
        // Controls remain enabled for manual interaction
        // this.controls.enabled = controlsWereEnabled;
        
        // Clear flag after auto-rotation update
        this.autoRotationUpdating = false;
        
        // Update current angle for overlay system - use sequence-based angle for consistent overlay behavior
        const sequenceAngle = this.autoRotationConfig.sequence[this.autoRotationConfig.sequenceIndex];
        this.currentAngle = sequenceAngle; // Use sequence angle for overlay system
    }
    
    updateSmoothTransition() {
        // Safety check for controls during transition
        if (!this.controls || !this.controls.target) {
            this.smoothTransitionActive = false;
            this.autoRotationUpdating = false;
            return;
        }
        
        // Set flag to prevent interaction detection during transition
        this.autoRotationUpdating = true;
        
        const currentTime = performance.now();
        const elapsed = currentTime - this.transitionStartTime;
        this.transitionProgress = Math.min(elapsed / this.transitionDuration, 1);
        
        // Use ease-out cubic for smooth, natural feeling transition
        const easedProgress = 1 - Math.pow(1 - this.transitionProgress, 3);
        
        // Interpolate between current position and target auto-rotation position
        const targetAngleRadians = (this.autoRotationAngle * Math.PI) / 180;
        const targetDistance = this.autoRotationDistance;
        
        // Calculate target position for auto-rotation
        const targetPosition = {
            x: this.controls.target.x + targetDistance * Math.sin(targetAngleRadians),
            y: Math.max(this.groundConstraints.minHeight, this.deviceConfig.cameraHeight),
            z: this.controls.target.z + targetDistance * Math.cos(targetAngleRadians)
        };
        
        // Interpolate camera position smoothly
        this.camera.position.x = this.lerp(this.transitionStartState.position.x, targetPosition.x, easedProgress);
        this.camera.position.y = this.lerp(this.transitionStartState.position.y, targetPosition.y, easedProgress);
        this.camera.position.z = this.lerp(this.transitionStartState.position.z, targetPosition.z, easedProgress);
        
        // Interpolate target position if needed (only if we have a valid saved state)
        if (this.savedCameraState && this.savedCameraState.target) {
            this.controls.target.x = this.lerp(this.transitionStartState.target.x, this.savedCameraState.target.x, easedProgress);
            this.controls.target.y = this.lerp(this.transitionStartState.target.y, this.savedCameraState.target.y, easedProgress);
            this.controls.target.z = this.lerp(this.transitionStartState.target.z, this.savedCameraState.target.z, easedProgress);
        }
        
        // Apply ground constraints during transition
        this.enforceGroundConstraints();
        
        // Update camera to look at target
        this.camera.lookAt(this.controls.target);
        this.camera.up.set(0, 1, 0);
        
        // Check if transition is complete
        if (this.transitionProgress >= 1) {
            this.smoothTransitionActive = false;
            this.autoRotationActive = true;
            this.autoRotationUpdating = false;
        }
    }
    
    pauseAutoRotation() {
        if (!this.autoRotationConfig.pauseOnInteraction) return;
        
        // Don't pause if auto-rotation is currently updating
        if (this.autoRotationUpdating) return;
        
        this.lastUserInteractionTime = performance.now();
        
        // Clear existing resume timer
        if (this.resumeAutoRotationTimer) {
            clearTimeout(this.resumeAutoRotationTimer);
        }
        
        // Set new resume timer with enhanced state restoration
        this.resumeAutoRotationTimer = setTimeout(() => {
            if (this.autoRotationConfig.enabled && !this.isRotating) {
                this.resumeAutoRotationWithTransition();
            }
        }, this.autoRotationConfig.resumeDelay);
        
        // Temporarily pause auto-rotation
        this.autoRotationActive = false;
    }
    
    resumeAutoRotationWithTransition() {
        // Always use optimal distance for auto-rotation
        this.autoRotationDistance = this.deviceConfig.optimalDistance;
        
        if (this.savedCameraState.timestamp > 0) {
            // Use saved camera state for smooth transition
            this.autoRotationAngle = this.savedCameraState.angle;
            
            // Update the auto-rotation sequence to start from the saved angle
            this.updateAutoRotationSequenceFromAngle(this.savedCameraState.angle);
            
            // Only start smooth transition if we have a valid saved state
            this.startSmoothTransition();
        } else {
            // Fallback: start auto-rotation immediately without transition
            this.autoRotationAngle = this.getCurrentCameraAngle();
            
            // Update the auto-rotation sequence to start from the current angle
            this.updateAutoRotationSequenceFromAngle(this.autoRotationAngle);
            
            // Start auto-rotation immediately (no smooth transition)
            this.autoRotationActive = true;
        }
    }
    
    startSmoothTransition() {
        // Prevent multiple smooth transitions from running simultaneously
        if (this.smoothTransitionActive) {
            console.warn('Smooth transition already active, skipping new transition');
            return;
        }
        
        this.smoothTransitionActive = true;
        this.transitionProgress = 0;
        this.transitionStartTime = performance.now();
        
        // Store current camera state as transition start
        if (!this.controls || !this.controls.target) {
            return;
        }
        
        this.transitionStartState = {
            position: { ...this.camera.position },
            target: { ...this.controls.target },
            distance: Math.sqrt(
                Math.pow(this.camera.position.x - this.controls.target.x, 2) + 
                Math.pow(this.camera.position.z - this.controls.target.z, 2)
            ),
            angle: this.getCurrentCameraAngle()
        };
    }
    
    
    setupOverlaySystem() {
        // Initialize overlay system (updated to match new camera preset angles)
        this.overlays = {
            'overlay-about': { angle: 270, element: document.getElementById('overlay-about') },
            'overlay-services': { angle: 0, element: document.getElementById('overlay-services') },
            'overlay-projects': { angle: 90, element: document.getElementById('overlay-projects') },
            'overlay-contact': { angle: 180, element: document.getElementById('overlay-contact') }
        };
        
        this.currentActiveOverlay = null;
        this.lastDetectedAngle = -1; // Track last detected angle to prevent unnecessary updates
        this.angleThreshold = 35; // Degrees threshold for manual rotation - overlay stays visible within ±20° of target angles
        this.rotationAngleThreshold = 45; // Larger threshold during programmatic rotation for faster overlay appearance
        this.loadingComplete = false; // Track loading state
    }
    
    activateOverlaySystem() {
        // Called after loading is complete to show initial overlay
        this.loadingComplete = true;
        this.showOverlayForAngle(270); // Show About Us overlay
        
        // Show drag indicator after loading is complete
        this.showDragIndicator();
        
        // Start auto-rotation after everything is loaded
        setTimeout(() => {
            this.startAutoRotation();
        }, 2000); // Wait 2 seconds before starting auto-rotation
    }
    
    showDragIndicator() {
        const dragIndicator = document.getElementById('drag-indicator');
        if (dragIndicator) {
            // Show the indicator
            setTimeout(() => {
                dragIndicator.classList.add('visible');
                
                // Auto-hide after 6 seconds
                setTimeout(() => {
                    dragIndicator.classList.add('auto-hide');
                }, 3000);
            }, 500); // Small delay after loading completes
        }
    }
    
    hideDragIndicator() {
        const dragIndicator = document.getElementById('drag-indicator');
        if (dragIndicator) {
            dragIndicator.classList.remove('visible', 'auto-hide');
        }
    }
    
    getCurrentCameraAngle() {
        // Safety check - return default angle if controls not initialized
        if (!this.controls || !this.controls.target) {
            return 270; // Default angle (About Us position)
        }
        
        // Calculate current camera angle in degrees
        const cameraAngle = Math.atan2(
            this.camera.position.x - this.controls.target.x,
            this.camera.position.z - this.controls.target.z
        );
        return ((cameraAngle * 180 / Math.PI + 360) % 360);
    }
    
    findClosestTargetAngle(currentAngle, useRotationThreshold = false) {
        const targetAngles = [0, 90, 180, 270];
        let closestAngle = targetAngles[0];
        let minDistance = Math.abs(currentAngle - targetAngles[0]);
        
        for (const angle of targetAngles) {
            // Calculate shortest angular distance
            let distance = Math.abs(currentAngle - angle);
            if (distance > 180) {
                distance = 360 - distance;
            }
            
            if (distance < minDistance) {
                minDistance = distance;
                closestAngle = angle;
            }
        }
        
        // Use different thresholds for different scenarios
        const threshold = useRotationThreshold ? this.rotationAngleThreshold : this.angleThreshold;
        
        // Only return the closest angle if we're within the threshold
        // Manual rotation: ±20° threshold for stable overlay zones
        // Programmatic rotation: ±45° threshold for faster overlay appearance
        return minDistance <= threshold ? closestAngle : null;
    }
    
    scheduleOverlayUpdate(angle) {
        // Clear any pending overlay update
        if (this.overlayUpdateTimeout) {
            if (this.isRotating) {
                cancelAnimationFrame(this.overlayUpdateTimeout);
            } else {
                clearTimeout(this.overlayUpdateTimeout);
            }
        }
        
        // During rotation, use requestAnimationFrame for smoother updates
        if (this.isRotating) {
            this.overlayUpdateTimeout = requestAnimationFrame(() => {
                if (angle === -1) {
                    this.hideAllOverlays();
                } else {
                    this.showOverlayForAngle(angle);
                }
                this.overlayUpdateTimeout = null;
            });
        } else {
            // For manual control, use timeout as before
            this.overlayUpdateTimeout = setTimeout(() => {
                if (angle === -1) {
                    this.hideAllOverlays();
                } else {
                    this.showOverlayForAngle(angle);
                }
                this.overlayUpdateTimeout = null;
            }, 100);
        }
    }
    
    updateOverlayFromManualRotation() {
        if (!this.overlays || !this.loadingComplete) return; // Skip only during loading
        
        // During programmatic rotation, reduce overlay update frequency for smoother animation
        if (this.isRotating) {
            // Only update overlays every 10th frame during rotation (6fps instead of 60fps)
            if (!this.overlayUpdateFrameCounter) this.overlayUpdateFrameCounter = 0;
            this.overlayUpdateFrameCounter++;
            if (this.overlayUpdateFrameCounter < 10) return;
            this.overlayUpdateFrameCounter = 0;
        }
        
        const currentAngle = this.getCurrentCameraAngle();
        // Use larger threshold during programmatic rotation for faster overlay appearance
        const targetAngle = this.findClosestTargetAngle(currentAngle, this.isRotating);
        
        // Only update if we found a valid target angle and it's different from last detected
        if (targetAngle !== null && targetAngle !== this.lastDetectedAngle) {
            this.lastDetectedAngle = targetAngle;
            this.scheduleOverlayUpdate(targetAngle);
        } else if (targetAngle === null && this.lastDetectedAngle !== -1) {
            // We're between angles, hide all overlays
            this.scheduleOverlayUpdate(-1); // -1 means hide all overlays
            this.lastDetectedAngle = -1;
        }
    }
    
    hideAllOverlays() {
        // Hide current overlay when between angles
        if (this.currentActiveOverlay && this.overlays[this.currentActiveOverlay]) {
            this.overlays[this.currentActiveOverlay].element.classList.remove('active');
            this.currentActiveOverlay = null;
        }
        
        // Remove active state from all navigation items
        document.querySelectorAll('.nav-menu a').forEach(link => {
            link.classList.remove('active');
        });
    }
    
    showOverlayForAngle(angle) {
        // Normalize angle to 0-360 range
        const normalizedAngle = ((angle % 360) + 360) % 360;
        
        // Find matching overlay
        let targetOverlay = null;
        for (const [id, overlay] of Object.entries(this.overlays)) {
            if (overlay.angle === normalizedAngle) {
                targetOverlay = { id, ...overlay };
                break;
            }
        }
        
        // Don't change if already showing the correct overlay
        if (this.currentActiveOverlay === targetOverlay?.id) {
            return;
        }
        
        // Use document fragment for batched DOM updates to reduce reflow
        const fragment = document.createDocumentFragment();
        
        // Hide current overlay
        if (this.currentActiveOverlay && this.overlays[this.currentActiveOverlay]) {
            this.overlays[this.currentActiveOverlay].element.classList.remove('active');
        }
        
        // Show new overlay
        if (targetOverlay && targetOverlay.element) {
            // During rotation, delay the appearance slightly to coordinate with camera movement
            if (this.isRotating) {
                // Use a shorter delay during rotation for responsiveness
                setTimeout(() => {
                    targetOverlay.element.classList.add('active');
                }, 50);
            } else {
                targetOverlay.element.classList.add('active');
            }
            
            this.currentActiveOverlay = targetOverlay.id;
            
            // Update active navigation state (batch this update)
            requestAnimationFrame(() => {
                this.updateActiveNavigation(normalizedAngle);
            });
        } else {
            this.currentActiveOverlay = null;
        }
    }
    
    updateActiveNavigation(angle) {
        // Remove active class from all nav items
        document.querySelectorAll('.nav-menu a').forEach(link => {
            link.classList.remove('active');
        });
        
        // Add active class to matching nav item
        const activeLink = document.querySelector(`.nav-menu a[data-angle="${angle}"]`);
        if (activeLink) {
            activeLink.classList.add('active');
        }
    }
    
    async initializeWithProgress() {
        // Start the loading process
        this.updateLoadingProgress(0, 'Initializing scene...');
        
        await this.delay(300);
        this.init();
        this.updateLoadingProgress(1, 'Setting up lighting...');
        
        await this.delay(400);
        this.setupLighting();
        this.updateLoadingProgress(2, 'Creating sky and water environment...');
        
        await this.delay(300);
        this.setupSkyWaterScene();
        
        // Conditionally setup seagull flock based on device capabilities
        if (this.shouldEnableSeagulls()) {
            this.updateLoadingProgress(3, 'Setting up seagull flock...');
            await this.delay(300);
            this.setupSeagullFlock();
            this.updateLoadingProgress(4, 'Setting up audio environment...');
        } else {
            this.updateLoadingProgress(3, 'Setting up audio environment...');
        }
        
        await this.delay(300);
        await this.setupAudioSystem();
        
        const nextStep = this.shouldEnableSeagulls() ? 5 : 4;
        this.updateLoadingProgress(nextStep, 'Setting up environment...');
        
        await this.delay(500);
        this.setupEnvironment();
        this.updateLoadingProgress(nextStep + 1, 'Configuring controls...');
        
        await this.delay(300);
        this.setupControls();
        this.setupNavigation();
        this.setupOverlaySystem();
        this.updateLoadingProgress(nextStep + 2, 'Loading Chromatic One Island...');
        
        // Wait for model loading to complete if it's active
        await this.waitForModelLoading();
        
        await this.delay(300);
        this.updateLoadingProgress(nextStep + 3, 'Finalizing setup...');
        
        await this.delay(400);
        this.animate();
        this.updateLoadingProgress(nextStep + 4, 'Complete!');
        
        // Hide loading screen after completion
        setTimeout(() => {
            document.getElementById('loading').style.opacity = '0';
            setTimeout(() => {
                document.getElementById('loading').style.display = 'none';
                // Activate overlay system after loading is completely done
                this.activateOverlaySystem();
            }, 500);
        }, 500);
    }
    
    updateLoadingProgress(stage, stageText, forcePercentage = null) {
        let percentage;
        
        if (forcePercentage !== null) {
            // Use forced percentage (for model loading)
            percentage = forcePercentage;
        } else {
            // Calculate percentage from stage
            percentage = Math.round((stage / this.loadingProgress.total) * 100);
        }
        
        // Ensure progress never goes backward
        if (percentage > this.loadingProgress.currentPercentage) {
            this.loadingProgress.currentPercentage = percentage;
            
            const loadingBar = document.querySelector('.loading-bar-fill');
            const loadingPercentage = document.querySelector('.loading-percentage');
            
            if (loadingBar) loadingBar.style.width = `${percentage}%`;
            if (loadingPercentage) loadingPercentage.textContent = `${percentage}%`;
        }
        
        // Always update stage text
        const loadingStage = document.querySelector('.loading-stage');
        if (loadingStage) loadingStage.textContent = stageText;
    }
    
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    
    waitForModelLoading() {
        return new Promise(resolve => {
            const checkModelLoading = () => {
                if (!this.loadingProgress.modelLoadingActive) {
                    resolve();
                } else {
                    setTimeout(checkModelLoading, 100);
                }
            };
            checkModelLoading();
        });
    }

    onWindowResize() {
        // Update device configuration if device type changed
        const configUpdated = this.updateDeviceConfiguration();
        
        // Update camera aspect ratio
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        
        // Update renderer size
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    animate() {
        if (this.isDestroying) return;
        
        this.frameId = requestAnimationFrame(() => this.animate());
        
        try {
            // Update camera rotation for navigation
            this.updateCameraRotation();
            
            // Update continuous auto-rotation
            this.updateContinuousAutoRotation();
            
            // Update controls if they exist
            if (this.controls) {
                this.controls.update();
            }
            
            // Enforce camera height constraint - never allow camera below ground level
            this.enforceGroundConstraints();
            
            // Update overlays based on manual camera rotation (throttled)
            this.updateOverlayFromManualRotation();
            
            // Update sky and water scene
            if (this.skyWaterScene) {
                this.skyWaterScene.update(this.camera.position);
            }
            
            // Update seagull flock (only if enabled and loaded)
            if (this.seagullFlock) {
                // Additional Android safety check in animation loop
                const userAgent = navigator.userAgent.toLowerCase();
                if (userAgent.includes('android')) {
                    console.warn('🚫 Android detected in animation loop - disabling seagulls');
                    this.seagullFlock = null;
                    return;
                }
                
                try {
                    this.seagullFlock.update();
                } catch (error) {
                    console.warn('Seagull flock update error:', error);
                    // Disable seagulls if they're causing issues
                    this.seagullFlock = null;
                }
            }
            
            // Render the scene
            this.renderer.render(this.scene, this.camera);
        } catch (error) {
            log.error('Animation frame error:', error);
        }
    }
}

// Initialize the scene when the page loads
let citySceneInstance = null;
document.addEventListener('DOMContentLoaded', () => {
    citySceneInstance = new CityScene();
    // Make it globally accessible for chat controls
    window.cityScene = citySceneInstance;
});

// SEO and Analytics tracking
document.addEventListener('DOMContentLoaded', () => {
    // Track page load time for Core Web Vitals
    window.addEventListener('load', () => {
        const loadTime = performance.now();
        
        // Send to analytics (implement your analytics here)
        if (typeof gtag !== 'undefined') {
            gtag('event', 'page_load_time', { 'value': Math.round(loadTime) });
            gtag('event', 'page_view', {
                'page_title': 'Home | Chromatic One',
                'page_location': window.location.href
            });
        }
    });
    
    // Track user interactions for engagement metrics
    const trackInteraction = (action, category = 'User Interaction') => {
        if (typeof gtag !== 'undefined') {
            gtag('event', action, {
                'event_category': category,
                'event_label': action
            });
        }
    };
    
    // Track navigation usage
    document.querySelectorAll('.nav-menu a').forEach(link => {
        link.addEventListener('click', () => {
            const section = link.textContent.toLowerCase().replace(' ', '_');
            trackInteraction(`navigation_${section}`, 'Navigation');
        });
    });
    
    // Track 3D interactions
    const canvas = document.getElementById('three-canvas');
    if (canvas) {
        canvas.addEventListener('mousedown', () => trackInteraction('3d_camera_control', '3D Interaction'));
        canvas.addEventListener('wheel', () => trackInteraction('3d_zoom', '3D Interaction'));
        canvas.addEventListener('touchstart', () => trackInteraction('3d_touch_control', '3D Interaction'));
    }
    
    // Track help system usage
    const helpIcon = document.getElementById('help-icon');
    if (helpIcon) {
        helpIcon.addEventListener('click', () => trackInteraction('help_opened', 'Help System'));
    }
    
    // Track loading completion
    setTimeout(() => {
        const loadingElement = document.getElementById('loading');
        if (loadingElement && loadingElement.style.display === 'none') {
            trackInteraction('3d_scene_loaded', 'Performance');
        }
    }, 5000);
});
